import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { HelpCircle, Loader, CheckCircle, Circle } from 'lucide-react';
import { 
  Header,
  Body,
  ButtonGroup,
  Button,
  LoadingOverlay 
} from '../StyledComponents';
import { animations } from '../animations';

// Enhanced styled components for better UX
const QuestionContainer = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 2px solid #f1f5f9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #e0e7ff;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #1d4ed8);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &.answered::before {
    opacity: 1;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
    background: #f8fafc;
    border-color: #e2e8f0;
  }

  &.disabled::before {
    display: none;
  }
`;

const QuestionTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
  padding-right: 2rem;
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OptionLabel = styled(motion.label)`
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  position: relative;
  min-height: 60px;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
    transform: translateX(4px);
  }

  &.selected {
    border-color: #3b82f6;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }

  &.selected:hover {
    border-color: #2563eb;
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  }

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const CustomRadio = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  margin-right: 1rem;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #3b82f6;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.2s ease;
  }

  .selected & {
    border-color: #3b82f6;
    background: white;
  }

  .selected &::after {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const CustomCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  margin-right: 1rem;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .selected & {
    border-color: #3b82f6;
    background: #3b82f6;
  }

  svg {
    color: white;
    opacity: 0;
    transition: opacity 0.2s ease;
    width: 14px;
    height: 14px;
  }

  .selected & svg {
    opacity: 1;
  }
`;

const OptionText = styled.span`
  font-size: 1rem;
  color: #334155;
  line-height: 1.5;
  font-weight: 500;
  flex: 1;

  .selected & {
    color: #1e40af;
  }
`;

const ProgressIndicator = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &.completed {
    background: #3b82f6;
    color: white;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const QuestionCounter = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 2rem;
  display: inline-block;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
`;

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 3rem 0 2rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  }
  
  span {
    margin: 0 2rem;
    font-weight: 600;
    color: #64748b;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const SkipNotice = styled(motion.div)`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    color: #d97706;
    flex-shrink: 0;
  }
  
  p {
    margin: 0;
    color: #92400e;
    font-weight: 500;
    font-size: 0.9rem;
  }
`;

export const QuestionsStep = ({
  questions,
  userAnswers,
  onOptionChange,
  onPrevious,
  onNext,
  isLoading,
  criticalIds
}) => {
  const yesNoQuestions = questions.filter(q => q.type !== 101);
  const multipleChoiceQuestions = questions.filter(q => q.type === 101);

  // Check if both yes/no questions are answered with "No"
  const checkIfBothYesNoAnsweredNo = () => {
    if (yesNoQuestions.length !== 2) return false;
    
    return yesNoQuestions.every(question => {
      const answer = userAnswers[question.question_id];
      if (!answer?.optionIds?.length) return false;
      
      // Find the selected option
      const selectedOption = question.question_options?.find(
        opt => answer.optionIds.includes(opt.option_id)
      );
      
      // Check if the selected option text contains "No" (case insensitive)
      return selectedOption?.text?.toLowerCase().includes('no');
    });
  };

  const bothYesNoAnsweredNo = checkIfBothYesNoAnsweredNo();
  const areMultipleChoiceDisabled = bothYesNoAnsweredNo;

  // Modified validation logic
  const allAnswered = () => {
    // Check if all yes/no questions are answered
    const yesNoAnswered = yesNoQuestions.every(question => {
      const answer = userAnswers[question.question_id];
      return answer?.optionIds?.length > 0;
    });

    if (!yesNoAnswered) return false;

    // If both yes/no questions are answered with "No", multiple choice is optional
    if (bothYesNoAnsweredNo) return true;

    // Otherwise, check if multiple choice questions are answered
    const multipleChoiceAnswered = multipleChoiceQuestions.every(question => {
      const answer = userAnswers[question.question_id];
      return answer !== undefined;
    });

    return multipleChoiceAnswered;
  };

  const canProceed = allAnswered();

  // Calculate answered count
  const getAnsweredCount = () => {
    let count = 0;
    
    // Count yes/no questions
    yesNoQuestions.forEach(question => {
      const answer = userAnswers[question.question_id];
      if (answer?.optionIds?.length > 0) count++;
    });

    // Count multiple choice questions (only if not disabled)
    if (!areMultipleChoiceDisabled) {
      multipleChoiceQuestions.forEach(question => {
        const answer = userAnswers[question.question_id];
        if (answer !== undefined) count++;
      });
    }

    return count;
  };

  const answeredCount = getAnsweredCount();
  const totalRequiredQuestions = areMultipleChoiceDisabled ? yesNoQuestions.length : questions.length;

  return (
    <motion.div {...animations.pageTransition}>
      <Header>
        <HelpCircle />
        <h2>Medical Assessment</h2>
      </Header>
      <Body>
        {isLoading && (
          <LoadingOverlay>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Loader />
            </motion.div>
            <p>Processing your responses...</p>
          </LoadingOverlay>
        )}

        <QuestionCounter>
          {answeredCount} of {totalRequiredQuestions} questions completed
        </QuestionCounter>

        {/* Yes/No Questions Section */}
        {yesNoQuestions.length > 0 && (
          <>
            {yesNoQuestions.map((question, index) => {
              const isAnswered = userAnswers[question.question_id]?.optionIds?.length > 0;
              
              return (
                <QuestionContainer
                  key={question?.question_id}
                  className={isAnswered ? 'answered' : ''}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ProgressIndicator className={isAnswered ? 'completed' : ''}>
                    {isAnswered ? <CheckCircle /> : <Circle />}
                  </ProgressIndicator>
                  
                  <QuestionTitle>{question?.text}</QuestionTitle>
                  
                  <OptionContainer>
                    {question?.question_options?.map(option => {
                      const isSelected = userAnswers[question.question_id]?.optionIds.includes(option.option_id);

                      return (
                        <OptionLabel
                          key={option.option_id}
                          className={isSelected ? 'selected' : ''}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => onOptionChange(
                            question.question_id,
                            option.option_id,
                            option.severity_level,
                            question.type
                          )}
                        >
                          <input
                            type="radio"
                            name={`question_${question.question_id}`}
                            checked={isSelected}
                            readOnly
                          />
                          <CustomRadio />
                          <OptionText>{option.text}</OptionText>
                        </OptionLabel>
                      );
                    })}
                  </OptionContainer>
                </QuestionContainer>
              );
            })}
          </>
        )}

        {/* Skip Notice */}
        {bothYesNoAnsweredNo && (
          <SkipNotice
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <CheckCircle size={20} />
            <p>Since you answered "No" to both questions above, the multiple choice questions below are optional. You can proceed directly or answer them for more detailed assessment.</p>
          </SkipNotice>
        )}

        {/* Section Divider */}
        {yesNoQuestions.length > 0 && multipleChoiceQuestions.length > 0 && (
          <SectionDivider>
            <span>
              Multiple Selection Questions 
              {areMultipleChoiceDisabled && <span style={{ fontSize: '0.7rem', opacity: 0.7 }}> (Optional)</span>}
            </span>
          </SectionDivider>
        )}

        {/* Multiple Choice Questions Section */}
        {multipleChoiceQuestions.map((question, index) => {
          const answer = userAnswers[question.question_id];
          const isAnswered = answer !== undefined;
          
          return (
            <QuestionContainer
              key={question.question_id}
              className={`${isAnswered ? 'answered' : ''} ${areMultipleChoiceDisabled ? 'disabled' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (yesNoQuestions.length + index) * 0.1 }}
            >
              <ProgressIndicator className={isAnswered ? 'completed' : ''}>
                {isAnswered ? <CheckCircle /> : <Circle />}
              </ProgressIndicator>
              
              <QuestionTitle>
                {question.text}
                <span style={{ 
                  fontSize: '0.75rem', 
                  color: '#64748b', 
                  fontWeight: '400',
                  marginLeft: '0.5rem',
                  background: '#f1f5f9',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px'
                }}>
                  Select all that apply {areMultipleChoiceDisabled && '(Optional)'}
                </span>
              </QuestionTitle>
              
              <OptionContainer>
                {question.question_options?.map(option => {
                  const isSelected = userAnswers[question.question_id]?.optionIds?.includes(option.option_id) || false;
                  
                  return (
                    <OptionLabel
                      key={option.option_id}
                      className={isSelected ? 'selected' : ''}
                      whileHover={areMultipleChoiceDisabled ? {} : { scale: 1.01 }}
                      whileTap={areMultipleChoiceDisabled ? {} : { scale: 0.99 }}
                      onClick={() => {
                        if (!areMultipleChoiceDisabled) {
                          onOptionChange(
                            question.question_id,
                            option.option_id,
                            option.severity_level,
                            question.type
                          );
                        }
                      }}
                      style={{
                        cursor: areMultipleChoiceDisabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          if (!areMultipleChoiceDisabled) {
                            onOptionChange(
                              question.question_id,
                              option.option_id,
                              option.severity_level,
                              question.type
                            );
                          }
                        }}
                        disabled={areMultipleChoiceDisabled}
                        style={{
                          width: '20px',
                          height: '20px',
                          marginRight: '1rem',
                          cursor: areMultipleChoiceDisabled ? 'not-allowed' : 'pointer',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <CustomCheckbox>
                        <CheckCircle />
                      </CustomCheckbox>
                      <OptionText>{option.text}</OptionText>
                    </OptionLabel>
                  );
                })}
              </OptionContainer>
            </QuestionContainer>
          );
        })}

        <ButtonGroup>
          <Button 
            variant="secondary" 
            onClick={onPrevious}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Previous
          </Button>
          <Button
            variant="primary"
            onClick={onNext}
            disabled={!canProceed || isLoading}
            whileHover={!canProceed ? {} : { scale: 1.02 }}
            whileTap={!canProceed ? {} : { scale: 0.98 }}
            style={{
              background: canProceed 
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                : undefined,
              opacity: canProceed ? 1 : 0.6
            }}
          >
            {canProceed 
              ? 'Continue' 
              : `Answer ${totalRequiredQuestions - answeredCount} more question${totalRequiredQuestions - answeredCount === 1 ? '' : 's'}`
            }
          </Button>
        </ButtonGroup>
      </Body>
    </motion.div>
  );
};